import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  pulseChainTest,
  pulseDevices,
  pulseFallback,
  pulseForecasts,
  pulseHandover,
  pulseHandoverSign,
  pulseHealth,
  pulseCanary,
  pulseIncidentAck,
  pulseIncidentsV3,
  pulsePaths,
  pulseProblemAck,
  pulseProblemTake,
  pulseProblems,
  pulseQaRuns,
  pulseReveal,
  pulseV3Facilities,
  type BuildingCard,
  type DevicesReport,
  type HealthCheck,
  type IncidentV3,
  type PathRun,
  type PathsReport,
  type PulseForecast,
  type PulseProblem,
  type UncoveredResident,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import {
  BTN_DARK,
  BTN_LIGHT,
  CARD,
  PC,
  QC,
  agoShort,
  dur,
  env,
  hm,
  idemKey,
  pathChip,
  pre,
  qChip,
  reducedMotion,
  secsWords,
  usePulseApi,
  type PKey,
  type QKey,
} from './lib';
import { StationProblemDrawer } from './StationDrawer';
import { RoleChecks } from './RoleChecks';
import { useCustomerText } from '../../ui/buildLabels';
import { plainProblemTitle } from './plainTitle';
import { carriesWords } from './incidentExtra';
import { tierLabel } from '../residents/common';

/**
 * A drill's name as shown, in plain words (V7 Safety drills), from the care canary's step ids
 * (src/aithora/pulse/canary.ts). Runs recorded before the rename carry 'companion-path' (the
 * old product word). A step this list does not know shows its id in words.
 */
export const DRILL_WORDS: Record<string, string> = {
  'db-read': 'The database answers',
  'health-rollup': 'Every building reports its health',
  'sensor-ingest': 'Sensor readings arrive from every building',
  'sensor-data-quality': 'Sensor readings make sense',
  'notification-delivery': 'Alerts and texts are delivered',
  'atlas-path': 'Atlas answers a resident',
  'companion-path': 'Atlas answers a resident',
};
export const drillName = (n: string) => DRILL_WORDS[n] ?? n.replace(/[-_]+/g, ' ');

/** The five safety paths the map draws, and the back-end paths behind each column. */
export const PATH_COLS: { label: string; every: string; keys: PathRun['path'][] }[] = [
  { label: 'Emergency & falls', every: 'Test event every 1 min', keys: ['emergency', 'falls'] },
  { label: 'Atlas check-in', every: 'Test every 5 min', keys: ['companion'] },
  { label: 'Guardian signal', every: 'Test every 5 min', keys: ['guardian_signal'] },
  { label: 'Med reminder', every: 'Test every 5 min', keys: ['med_reminder'] },
  { label: 'Chart write-back', every: 'Test every 5 min', keys: ['ehr_sync'] },
];

const RANKP: Record<PKey, number> = { down: 0, degraded: 1, unknown: 2, ok: 3, na: 4 };
export function runState(r: PathRun | null | undefined): PKey {
  if (!r) return 'na';
  if (r.state) return r.state;
  return r.ok === true ? 'ok' : r.ok === false ? 'down' : 'na';
}
/** The cell for one column: the worst of the paths behind it. */
export function colRun(p: PathsReport | null | undefined, keys: PathRun['path'][]): PathRun | null {
  const runs = (p?.paths ?? []).filter((r) => keys.includes(r.path));
  if (!runs.length) return null;
  return runs.slice().sort((a, b) => RANKP[runState(a)] - RANKP[runState(b)])[0];
}
export function cellNote(r: PathRun | null, col: number, now: number): string {
  const k = runState(r);
  if (!r) return 'not tested yet';
  if (k === 'ok') return agoShort(r.last_run_at, now) + (col === 0 && r.duration_ms != null ? ' · ' + (r.duration_ms / 1000).toFixed(1) + 's' : '');
  if (r.note) return r.note;
  const i = (r.steps ?? []).findIndex((s) => !s.ok);
  if (i >= 0) return 'fails at step ' + (i + 1) + ' · ' + (r.steps as NonNullable<PathRun['steps']>)[i].detail;
  return k === 'na' ? 'not connected' : 'not tested yet';
}

/** "Mae Lawson" -> "Mae L." (the station's short form). */
export function shortPerson(name: string): string {
  const w = name.trim().split(/\s+/);
  return w.length > 1 ? w[0] + ' ' + w[w.length - 1][0].toUpperCase() + '.' : w[0];
}
function residentLabel(u: UncoveredResident, revealed: string | null): { who: string; masked: boolean } {
  const room = u.resident.room ? ' · Rm ' + u.resident.room : '';
  if (revealed) return { who: shortPerson(revealed) + room, masked: false };
  if (u.resident.name) return { who: shortPerson(u.resident.name) + room, masked: false };
  return { who: (u.resident.initials ?? '') + room, masked: true };
}

/** The device kinds, in the order deviceCells returns them. */
export const DEVICE_KINDS = ['Wearables worn', 'Bed / chair', 'Room / door', 'Tablets', 'Staff phones'];

type DevCell = { v: string; tip: string; k: 'ok' | 'degraded' | 'down' };
export function deviceCells(d: DevicesReport | null): DevCell[] {
  const devs = d?.devices ?? [];
  const cell = (good: number, total: number, what: string): DevCell => {
    const k = total > 0 && good === total ? 'ok' : good === 0 && total > 0 ? 'down' : 'degraded';
    return { v: good + ' of ' + total, k: total === 0 ? 'ok' : k, tip: good === total ? 'all checking in every 60s' : total - good + ' ' + what };
  };
  const of = (kind: string) => devs.filter((x) => x.kind === kind);
  const w = of('wearable');
  return [
    cell(w.filter((x) => x.state === 'online' && x.worn !== false).length, w.length, 'not worn or offline'),
    ...(['bed_chair', 'room_door', 'tablet', 'staff_phone'] as const).map((k) => cell(of(k).filter((x) => x.state === 'online').length, of(k).length, 'offline')),
  ];
}

const PROBLEM_RANK: Record<QKey, number> = { down: 0, degraded: 1, unknown: 2, ok: 3 };

interface Acts {
  label: (p: PulseProblem) => string;
  run: (p: PulseProblem) => void;
}

/**
 * The Live station (design psMain, T7364-7504): command bar, safety paths, devices and
 * wearables, Aithora systems, Act now and Coming up, with the problem drawer, the path
 * drawer, the handover and the name reveal. A Facility Admin sees their own building only,
 * read-only, with no systems panel and no station actions (design psIsFA).
 */
export function Station({ role, canWrite, readOnlyReason, onOpenIncident }: { role: 'operator' | 'dev' | 'admin'; canWrite: boolean; readOnlyReason: string | null; onOpenIncident?: (incidentId: string) => void }) {
  const plain = useCustomerText();
  const FA = role === 'admin';
  const isDev = role === 'dev';
  const { client, session } = useSession();
  const [nowTick, setTick] = useState(0);
  const reduce = reducedMotion();
  // One shared 1s clock, like the design's (it drives the countdowns and the dot's pulse).
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const now = Date.now();

  const [rewind, setRewind] = useState(false);
  const [at, setAt] = useState(0);
  const past = at > 0;
  const [wall, setWall] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pathOpen, setPathOpen] = useState<{ org: string; col: number } | null>(null);
  const [handOpen, setHandOpen] = useState(false);
  const [onbOpen, setOnbOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [confirm, setConfirm] = useState<{ title: string; body: ReactNode; label: string; go: () => Promise<void> } | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [ask, setAsk] = useState<{ token: string; who: string } | null>(null);
  const [askWhy, setAskWhy] = useState('');
  const [actError, setActError] = useState<string | null>(null);

  const facs = usePulseApi((c, signal) => pulseV3Facilities(c, { signal }));
  const all: BuildingCard[] = facs.data?.facilities ?? [];
  const live = all.filter((f) => f.status === 'live');
  const onb = FA ? [] : all.filter((f) => f.status !== 'live');
  // Whose devices and fall coverage the page reads. A Facility Admin's own building is read
  // whether or not it is live yet, exactly as the System Pulse menu label counts it (both
  // come from the server's fallCoverage); before, an onboarding building read "0 without
  // fall coverage" here while the label beside it said Watch with the real count.
  const watched = FA ? all : live;
  const liveKey = watched.map((f) => f.organization_id).join(',');
  const detail = usePulseApi(
    (c, signal) =>
      Promise.all(
        watched.map(async (f) => ({
          id: f.organization_id,
          paths: await pulsePaths(c, { query: { facility: f.organization_id }, signal }).catch(() => null),
          devices: await pulseDevices(c, { query: { facility: f.organization_id }, signal }).catch(() => null),
        })),
      ),
    [liveKey],
    watched.length > 0,
  );
  const byOrg = useMemo(() => new Map((detail.data ?? []).map((d) => [d.id, d])), [detail.data]);
  const viewAt = past ? now - at * 60000 : now;
  // The rewound moment to the minute, so the 1s clock does not refetch every second.
  const atIso = past ? new Date(Math.floor(viewAt / 60000) * 60000).toISOString() : undefined;
  const probs = usePulseApi((c, signal) => pulseProblems(c, { query: atIso ? { at: atIso } : undefined, signal }), [atIso]);
  const fore = usePulseApi((c, signal) => pulseForecasts(c, { signal }));
  // V6 "Safety drills": the care canary's steps, run against every live building's care paths.
  const canary = usePulseApi((c, signal) => env(pulseCanary(c, { signal })), [], !FA);
  // V6 "Role checks": the latest QA crawl (Bugsy) from the console and the phone, per role.
  const qaRuns = usePulseApi((c, signal) => env(pulseQaRuns(c, { signal })), [], !FA);
  const health = usePulseApi((c, signal) => env(pulseHealth(c, { query: atIso ? { at: atIso } : undefined, signal })), [atIso], !FA);
  const incV3 = usePulseApi((c, signal) => pulseIncidentsV3(c, { signal }), [], !FA && openId !== null);
  const hand = usePulseApi((c, signal) => pulseHandover(c, { signal }), [], handOpen);

  const me = session?.user.id ?? null;
  const problems = useMemo(() => {
    const list = (probs.data?.problems ?? []).filter((p) => !FA || !p.organization_id || live.some((f) => f.organization_id === p.organization_id));
    // Clocks run from the moment on screen: now, or the rewound moment (design psVals remain / age).
    const remain = (p: PulseProblem) => (p.due_at ? (Date.parse(p.due_at) - viewAt) / 60000 : null);
    return list
      .map((p) => ({ p, remain: remain(p), age: (viewAt - Date.parse(p.started_at)) / 60000 }))
      .sort((a, b) => PROBLEM_RANK[a.p.severity] - PROBLEM_RANK[b.p.severity] || (a.remain ?? 1e9) - (b.remain ?? 1e9) || b.p.exposed - a.p.exposed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probs.data, FA, liveKey, viewAt]);
  const ownerOf = (p: PulseProblem) => (p.owner ? (p.owner.user_id && p.owner.user_id === me ? 'you' : p.owner.name) : '');
  const exposed = problems.reduce((x, q) => x + q.p.exposed, 0);
  const unowned = problems.filter((q) => !q.p.owner && q.p.severity !== 'unknown').length;
  const worst = problems[0] ?? null;
  const tone = worst ? QC[worst.p.severity][0] : '#1F6B52';

  const reload = () => {
    probs.reload();
    fore.reload();
  };
  const fail = (e: unknown) => setActError(e instanceof Error ? e.message : 'The request failed.');

  // ── actions (design doAct): take, tell the building, run chain tests, acknowledge ──
  const acts: Acts = {
    label: (p) => {
      const o = ownerOf(p);
      if (p.action === 'take') return o === 'you' ? 'Yours' : p.action_label;
      if (p.action === 'tell') return p.told_at ? 'Sent ' + hm(Date.parse(p.told_at)) : p.action_label;
      if (p.action === 'test') return p.tests_sent_at ? 'Tests sent' : p.action_label;
      return p.acknowledged ? 'Acknowledged' : p.action_label;
    },
    run: (p) => {
      if (past || !canWrite) return;
      setActError(null);
      if (p.action === 'take') {
        if (ownerOf(p) === 'you') return;
        pulseProblemTake(client, { params: { id: p.id } }).then(reload, fail);
      } else if (p.action === 'tell' && p.organization_id) {
        const org = p.organization_id;
        setConfirm({
          title: 'Send the downtime message to ' + (p.building ?? 'the building') + '?',
          body: 'The building contact gets the downtime message now. If nobody confirms within 15 minutes it goes to the Aithora on-call to phone them.',
          label: 'Send it',
          go: () => pulseFallback(client, { params: { id: org }, body: withConfirm({ idempotency_key: idemKey() }) }).then(reload),
        });
      } else if (p.action === 'test') {
        const ids = p.building_ids ?? (p.organization_id ? [p.organization_id] : []);
        setConfirm({
          title: 'Run a chain test in ' + ids.length + (ids.length === 1 ? ' building?' : ' buildings?'),
          body: 'A real test alert goes to the staff on shift, never to residents. Someone on shift has to acknowledge it for the path to count as proven.',
          label: 'Run chain tests',
          go: () => Promise.all(ids.map((id) => pulseChainTest(client, { params: { id }, body: withConfirm({ idempotency_key: idemKey() }) }))).then(reload),
        });
      } else if (!p.acknowledged) {
        const call = p.incident_id ? pulseIncidentAck(client, { params: { id: p.incident_id }, body: { confirm: true } }) : pulseProblemAck(client, { params: { id: p.id } });
        (call as Promise<unknown>).then(reload, fail);
      }
    },
  };
  const ack = (p: PulseProblem) => {
    if (past || !canWrite || p.acknowledged) return;
    const call = p.incident_id ? pulseIncidentAck(client, { params: { id: p.incident_id }, body: { confirm: true } }) : pulseProblemAck(client, { params: { id: p.id } });
    (call as Promise<unknown>).then(reload, fail);
  };

  // Keyboard (design psKey): J/K move, A ack, T take, Enter open, Esc closes.
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    const tg = t?.tagName;
    if (tg === 'INPUT' || tg === 'TEXTAREA' || tg === 'SELECT' || t?.isContentEditable) return;
    if (e.key === 'Escape') {
      setOpenId(null);
      setHandOpen(false);
      setPathOpen(null);
      return;
    }
    if (past || e.metaKey || e.ctrlKey) return;
    const i = Math.max(0, Math.min(problems.length - 1, sel));
    const p = problems[i]?.p;
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      setSel(Math.min(problems.length - 1, i + 1));
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      setSel(Math.max(0, i - 1));
    } else if (e.key === 'a' && p) ack(p);
    else if (e.key === 't' && p && p.action === 'take') acts.run(p);
    else if (e.key === 'Enter' && p) setOpenId(p.id);
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── derived blocks ───────────────────────────────────────────────────────
  const resLive = live.reduce((x, f) => x + (f.residents_enrolled ?? 0), 0);
  const uncovered = watched.flatMap((f) => (byOrg.get(f.organization_id)?.devices?.residents_without_fall_coverage ?? []).map((u) => ({ u, bld: f.name })));
  const uncCount = uncovered.length;
  const mapRows = live
    .map((f) => {
      const p = byOrg.get(f.organization_id)?.paths ?? null;
      const cells = PATH_COLS.map((c, i) => {
        const r = colRun(p, c.keys);
        return { r, k: runState(r), note: cellNote(r, i, now), col: i };
      });
      const rank = cells.some((c) => c.k === 'down') ? 0 : cells.some((c) => c.k === 'degraded') ? 1 : 2;
      return { f, cells, rank };
    })
    .sort((a, b) => a.rank - b.rank);
  const nextUnowned = problems.find((q) => !q.p.owner && q.p.action === 'take');
  const svc = useMemo(() => {
    const checks: HealthCheck[] = (health.data?.groups ?? []).flatMap((g) => g.checks);
    const RANK: Record<string, number> = { down: 0, degraded: 1, unknown: 2, ok: 3 };
    return checks.map((c, i) => ({ c, i, rank: RANK[c.status] ?? 2 })).sort((a, b) => a.rank - b.rank || a.i - b.i);
  }, [health.data]);

  // The headline names the problem in plain words (V7: "38 residents exposed right now · Shady Acres · Atlas silent").
  const title = (p: PulseProblem) => plainProblemTitle(p.title);
  // A Facility Admin whose building is still onboarding: nothing reads green (the
  // operator's rule for onboarding buildings). Before, an empty page said "Every resident
  // covered, every safety path OK" over 0 residents.
  const faOnboarding = FA && !facs.loading && live.length === 0 && all.length > 0;
  const psHead = faOnboarding
    ? all[0].name + ' is not live yet'
    : FA
    ? uncCount
      ? uncCount + ' of your residents without fall coverage right now'
      : 'Every resident covered, every safety path OK'
    : exposed
      ? exposed + ' residents exposed ' + (past ? 'at ' + hm(now - at * 60000) : 'right now') + ' · ' + title(problems.slice().sort((a, b) => b.p.exposed - a.p.exposed)[0].p)
      : worst
        ? title(worst.p)
        : probs.error
          ? 'The problem queue is not connected yet'
          : 'Every building covered, every service OK';
  const psCause = faOnboarding
    ? 'Onboarding. Not live, so nothing here reads green or counts as healthy. Finish setup on the Overview to go live.'
    : FA
    ? (live[0]?.name ?? 'Your building') + ' only. Aithora watches its own systems around the clock and tells you here if anything affects your building.' + (problems.length ? '' : ' No Aithora problem is affecting you.')
    : probs.error
      ? plain('GET /pulse-v3/problems is not served yet, so nothing here claims a building is covered.')
      : problems.length
        ? (worst && worst.remain !== null ? 'Soonest deadline: ' + (worst.p.source_id ? worst.p.incident_ref ?? worst.p.title : worst.p.title.split(' · ')[0]) + ' ' + worst.p.due_what + ' in ' + dur(worst.remain) + '. ' : '') + problems.length + ' problems, worst first, symptoms grouped by cause.'
        : 'Nothing open.';
  const lastBeat = probs.data?.last_beat_at ? Math.max(0, Math.round((now - Date.parse(probs.data.last_beat_at)) / 1000)) + 's' : '–';
  const stats: [string, string, string][] = FA
    ? [
        [String((live[0] ?? all[0])?.residents_enrolled ?? 0), 'residents enrolled', '#22272E'],
        [String(uncCount), 'without fall coverage', uncCount ? '#B3261E' : '#1F6B52'],
        [String(problems.length), 'open at your building', problems.length ? '#B3261E' : '#1F6B52'],
      ]
    : [
        [String(resLive), 'residents enrolled', '#22272E'],
        [String(exposed), 'not being checked on', exposed ? '#B3261E' : '#1F6B52'],
        [String(uncCount), 'without fall coverage', '#B3261E'],
        [String(problems.length), 'open', '#22272E'],
        [String(unowned), 'unowned', unowned ? '#B3261E' : '#1F6B52'],
        [past ? hm(now - at * 60000) : lastBeat, past ? 'viewing' : 'since last beat', '#22272E'],
      ];
  const faTone = faOnboarding ? '#54606C' : uncCount ? '#B3261E' : '#1F6B52';
  const cmdStyle = FA
    ? 'flex:0 0 auto;box-sizing:border-box;display:flex;flex-direction:column;gap:5px;padding:9px 13px;border-radius:12px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);box-shadow:inset 4px 0 0 ' + faTone + ';min-width:0;'
    : 'flex:0 0 auto;box-sizing:border-box;display:flex;flex-direction:column;gap:5px;padding:9px 13px;border-radius:12px;background:' + (past ? '#F1F5FB' : '#FCFAF4') + ';border:1px solid rgba(34,39,46,.14);box-shadow:inset 4px 0 0 ' + tone + ';min-width:0;';
  const dotStyle = FA
    ? 'flex:0 0 12px;width:12px;height:12px;border-radius:50%;background:' + faTone + ';'
    : 'flex:0 0 12px;width:12px;height:12px;border-radius:50%;background:' + tone + ';' + (worst && !reduce && !past ? 'box-shadow:0 0 0 ' + (nowTick % 2 ? 6 : 0) + 'px ' + tone + '22;transition:box-shadow .9s ease-out;' : '');
  const aBtn = (on: boolean) => 'display:inline-flex;align-items:center;height:30px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;' + (on ? 'background:#2F5C97;color:#fff;' : 'background:#fff;color:#22272E;border:1px solid rgba(34,39,46,.2);');
  const linkBtn = (on: boolean) => 'font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:' + (on ? '#2F5C97' : '#3C454F') + ';';
  const stationActs: { label: string; tip: string; on: boolean; go: () => void; primary?: boolean }[] = FA
    ? []
    : [
        { label: nextUnowned && !past ? 'Take next (T)' : 'Nothing unowned', tip: 'Take the most urgent unowned problem', on: false, primary: true, go: () => nextUnowned && !past && acts.run(nextUnowned.p) },
        { label: 'Handover', tip: 'Write the shift handover and sign to accept', on: false, go: () => setHandOpen(true) },
        { label: rewind ? 'Rewind on' : 'Rewind', tip: 'Replay what the station knew at any moment in the last 24h', on: rewind, go: () => { setRewind(!rewind); setAt(0); } },
        { label: wall ? 'Exit wall mode' : 'Wall mode', tip: 'Large type for the ops-room screen', on: wall, go: () => setWall(!wall) },
        // The Developer reads the station (Spec V4 14, psActs filter L19767): Take next and
        // Handover are hidden; Rewind and Wall mode stay.
      ].filter((_b, i) => !isDev || i >= 2);
  // psCanWrite (V4 L19761): every write on the station is hidden from the Developer.
  const psCanWrite = !isDev;

  const rootStyle = 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;gap:10px;min-width:0;padding-right:3px;' + (wall ? 'zoom:1.28;' : '');
  const selIdx = Math.max(0, Math.min(problems.length - 1, sel));
  const openProblem = openId ? problems.find((q) => q.p.id === openId) ?? null : null;
  const openInc: IncidentV3 | null = openProblem?.p.incident_id ? incV3.data?.incidents.find((i) => i.id === openProblem.p.incident_id) ?? null : null;
  const writeTitle = canWrite ? undefined : readOnlyReason ?? undefined;

  const pathDrawer = (() => {
    if (!pathOpen) return null;
    const f = live.find((x) => x.organization_id === pathOpen.org);
    if (!f) return null;
    const col = PATH_COLS[pathOpen.col];
    const r = colRun(byOrg.get(f.organization_id)?.paths ?? null, col.keys);
    const k = runState(r);
    const plan = r?.plan ?? (r?.steps ?? []).map((s) => ({ label: s.label ?? s.name, human: false }));
    const steps = r?.steps ?? [];
    const failAt = k === 'down' || k === 'degraded' ? steps.findIndex((s) => !s.ok) : -1;
    const note = r?.note ?? cellNote(r, pathOpen.col, now);
    const side = r?.side === 'building' ? 'building side' : r?.side && r.side !== 'aithora' ? r.side + ' side' : 'Aithora side';
    return {
      title: col.label + ' · ' + f.name,
      sub: col.every + '. Every step is timed; the first one that fails is named.',
      steps: plan.map((x, j) => {
        const kk: PKey = k === 'na' ? 'na' : j === failAt ? k : failAt >= 0 && j > failAt ? 'unknown' : x.human && !f.last_proven ? 'unknown' : 'ok';
        return {
          n: String(j + 1),
          t: x.label,
          k: kk,
          note: kk === 'ok' ? (x.human ? 'weekly human test passed' : 'passed in the last test') : kk === 'unknown' ? (x.human ? 'only a weekly human test can prove this' : 'not reached') : kk === 'na' ? 'not connected' : note + ' · ' + side,
        };
      }),
    };
  })();

  const busy = facs.loading || (watched.length > 0 && detail.loading) || probs.loading;

  return (
    <>
      <div data-screen-label="Pulse · Station" style={sx(rootStyle)} aria-busy={busy ? 'true' : undefined} data-testid="pulse-station">
        {/* Command */}
        <div data-screen-label="Pulse · Command" style={sx(cmdStyle)}>
          <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
            <span style={sx(dotStyle)} />
            <span style={sx('flex:1 1 360px;min-width:0;font-size:16.5px;font-weight:800;letter-spacing:-.02em;line-height:1.25;text-wrap:pretty;')}>
              <span>{psHead}</span>
            </span>
            {past && (
              <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.08em;color:#fff;background:#2F5C97;')}>
                REWIND {'·'} <span>{hm(now - at * 60000)}</span>
              </span>
            )}
          </div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
            <span>{psCause}</span>
          </div>
          <div style={sx('display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;min-width:0;')}>
            {stats.map(([v, k, c]) => (
              <span key={k} style={sx('font-size:14px;white-space:nowrap;color:' + c + ';')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-weight:800;')}>
                  <span>{v}</span>
                </span>{' '}
                <span>{k}</span>
              </span>
            ))}
            <span style={sx('flex:1 1 12px;')} />
            {(isDev || FA) && (
              <span data-testid="pulse-ro" style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                {'Read-only as ' + tierLabel(isDev ? 'DEVELOPER' : 'ADMIN')}
              </span>
            )}
            {stationActs.map((b, i) => (
              <a
                key={b.label}
                href="#"
                title={b.tip}
                aria-pressed={i > 1 || isDev ? b.on : undefined}
                onClick={(e) => {
                  pre(e);
                  b.go();
                }}
                style={sx(i < 1 && !isDev ? aBtn(b.on) : linkBtn(b.on))}
              >
                <span>{b.label}</span>
              </a>
            ))}
          </div>
          {rewind && !FA && (
            <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(34,39,46,.1);min-width:0;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#2F5C97;')}>Rewind</span>
              <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>24h ago</span>
              <input
                type="range"
                min={0}
                max={1440}
                step={5}
                value={at}
                aria-label="Rewind the station"
                onChange={(e) => {
                  setAt(Number(e.target.value) || 0);
                  setOpenId(null);
                }}
                style={sx('flex:1 1 280px;min-width:0;accent-color:#2F5C97;direction:rtl;')}
              />
              <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>now</span>
              <span style={sx('flex:1 1 240px;min-width:0;font-size:14px;color:#22272E;line-height:1.4;text-wrap:pretty;')}>
                <span>{past ? 'Showing ' + hm(now - at * 60000) + ': exactly what the station knew then, from the event record. Actions are off while rewound.' : 'Drag left to replay any moment in the last 24 hours.'}</span>
              </span>
              <a href="#" onClick={(e) => { pre(e); setAt(0); }} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:#fff;color:#2F5C97;border:1px solid rgba(47,92,151,.35);')}>
                Back to now
              </a>
            </div>
          )}
        </div>

        {actError && (
          <div role="alert" style={sx('font-size:14px;color:#B3261E;font-weight:700;')}>
            {actError}
          </div>
        )}

        {/* Safety paths */}
        <div data-screen-label="Pulse · Safety map" style={sx(CARD)} data-testid="pulse-safety-map">
          <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;margin-bottom:6px;')}>
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;color:#22272E;')}>Safety paths</span>
            <span style={sx('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>
              <span>A test event runs through each path in each building: emergency every 1 min, the rest every 5 min. Devices check in every 60s; 3 missed is offline. Click a cell to see which step failed.</span>
            </span>
          </div>
          <div style={sx('overflow-x:auto;min-width:0;')}>
            <div style={sx('min-width:760px;')}>
              <div style={sx('display:grid;grid-template-columns:minmax(0,1.3fr) repeat(5,minmax(0,1fr)) minmax(0,1.1fr);gap:4px;padding:0 0 4px;font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;font-size:14px;')}>
                <span>Building</span>
                {PATH_COLS.map((h) => (
                  <span key={h.label} title={h.every}>
                    <span>{h.label}</span>
                  </span>
                ))}
                <span>Human test (weekly)</span>
              </div>
              {mapRows.map(({ f, cells }) => {
                const proven = f.last_proven;
                const human = proven ? 'Proven ' + agoShort(proven.at, now) + (proven.ack_seconds != null ? ' · answered in ' + secsWords(proven.ack_seconds) : '') : 'Never proven · test not yet run';
                return (
                  <div key={f.organization_id} style={sx('display:grid;grid-template-columns:minmax(0,1.3fr) repeat(5,minmax(0,1fr)) minmax(0,1.1fr);gap:4px;padding:4px 0;border-top:1px solid rgba(34,39,46,.08);align-items:stretch;')}>
                    <span style={sx('min-width:0;display:flex;flex-direction:column;justify-content:center;')}>
                      <span style={sx('font-size:16px;font-weight:700;')}>
                        <span>{f.name}</span>
                      </span>
                      <span style={sx('font-size:14px;color:#54606C;')}>
                        <span>{(f.residents_enrolled ?? 0) + ' residents'}</span>
                      </span>
                    </span>
                    {cells.map((c) => {
                      const k = c.k;
                      const plain = k === 'ok' || k === 'na';
                      return (
                        <a
                          key={c.col}
                          href="#"
                          title={PATH_COLS[c.col].label + ' at ' + f.name + ': ' + c.note + '. ' + PATH_COLS[c.col].every + '.'}
                          onClick={(e) => {
                            pre(e);
                            setPathOpen({ org: f.organization_id, col: c.col });
                          }}
                          style={sx('display:flex;flex-direction:column;gap:2px;padding:5px 6px;border-radius:6px;text-decoration:none;min-width:0;box-sizing:border-box;color:#22272E;background:' + (plain ? '#fff' : PC[k][1]) + ';border:1px solid ' + (plain ? 'rgba(34,39,46,.1)' : PC[k][0] + '66') + ';')}
                        >
                          <span style={sx(pathChip(k))}>
                            <span>{PC[k][2]}</span>
                          </span>
                          <span style={sx('font-size:14px;line-height:1.3;color:#3C454F;overflow-wrap:break-word;')}>
                            <span>{c.note}</span>
                          </span>
                        </a>
                      );
                    })}
                    <span style={sx('align-self:center;font-size:14px;line-height:1.3;font-weight:700;color:' + (proven ? '#1F6B52' : '#B3261E') + ';')}>
                      <span>{human}</span>
                    </span>
                  </div>
                );
              })}
              {!FA && (
                <a href="#" onClick={(e) => { pre(e); setOnbOpen(!onbOpen); }} style={sx('display:block;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);font-size:14px;color:#54606C;text-decoration:none;')}>
                  <span>{onb.length + ' buildings onboarding. Not live, so nothing there reads green or counts as healthy. ' + (onbOpen ? 'Hide' : 'Show')}</span>
                </a>
              )}
              {onbOpen && (
                <div style={sx('display:flex;flex-wrap:wrap;gap:4px 12px;font-size:14px;color:#54606C;')}>
                  {onb.map((o) => (
                    <span key={o.organization_id}>
                      <span>{o.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr));gap:10px;min-width:0;align-items:start;')}>
          {/* Devices & wearables */}
          <div data-screen-label="Pulse · Devices" style={sx(CARD)} data-testid="pulse-devices">
            <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;margin-bottom:6px;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;color:#22272E;')}>Devices &amp; wearables</span>
              <span style={sx('font-size:14px;color:#54606C;')}>
                <span>Every device checks in every 60s. Worn is detected, not assumed.</span>
              </span>
            </div>
            {/* The design's table (V9 psDev): a row per building, the kinds as columns. The
                building name wraps rather than being cut off; a narrow card scrolls sideways. Each column
                keeps room for its longest header word ("WEARABLES"), where the design's let two overlap. */}
            <div style={sx('overflow-x:auto;min-width:0;')}>
              <div style={sx('min-width:430px;')}>
                <div style={sx('display:grid;grid-template-columns:minmax(110px,1.2fr) repeat(5,minmax(104px,1fr));gap:4px;font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;padding-bottom:4px;')}>
                  <span>Building</span>
                  {DEVICE_KINDS.map((k) => (
                    <span key={k}>{k}</span>
                  ))}
                </div>
                {watched.map((f) => (
                  <div key={f.organization_id} data-testid="pulse-devices-building" style={sx('display:grid;grid-template-columns:minmax(110px,1.2fr) repeat(5,minmax(104px,1fr));gap:4px;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);align-items:baseline;font-size:14px;')}>
                    <span style={sx('font-weight:700;min-width:0;overflow-wrap:break-word;')}>{f.name}</span>
                    {deviceCells(byOrg.get(f.organization_id)?.devices ?? null).map((c, i) => (
                      <span key={i} title={c.tip} style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:' + PC[c.k][0] + ';')}>
                        {c.v}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;margin:10px 0 4px;')}>Residents without fall coverage</div>
            {uncovered.map(({ u, bld }, i) => {
              const token = u.reveal_token ?? null;
              const r = residentLabel(u, token ? revealed[token] ?? null : null);
              const canReveal = isDev && r.masked && !!token;
              return (
                <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.3fr);gap:2px 10px;padding:5px 0;border-top:1px solid rgba(34,39,46,.07);font-size:14px;line-height:1.35;')}>
                  <span style={sx('min-width:0;overflow-wrap:break-word;')}>
                    {canReveal && (
                      <a
                        href="#"
                        onClick={(e) => {
                          pre(e);
                          setAsk({ token: token as string, who: r.who + ' · ' + bld });
                          setAskWhy('');
                        }}
                        title="Show name (logged)"
                        aria-label="Show name"
                        style={sx('display:inline-flex;vertical-align:-2px;margin-right:4px;color:#2F5C97;')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
                          <circle cx="12" cy="12" r="2.8" />
                        </svg>
                      </a>
                    )}
                    <span style={sx('font-weight:700;')}>
                      <span>{r.who}</span>
                    </span>{' '}
                    <span style={sx('color:#54606C;')}>
                      {'· '}
                      <span>{bld}</span>
                    </span>
                  </span>
                  <span style={sx('min-width:0;')}>
                    <span style={sx('color:#B3261E;font-weight:700;')}>
                      <span>{u.why ?? 'No working wearable or bed sensor'}</span>
                    </span>{' '}
                    <span style={sx('color:#3C454F;')}>
                      {'→ '}
                      <span>{u.fallback}</span>
                    </span>
                  </span>
                </div>
              );
            })}
            <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>
              <span>
                {detail.data && live.some((f) => byOrg.get(f.organization_id)?.devices?.residents_without_fall_coverage == null)
                  ? 'No census recorded for ' + live.filter((f) => byOrg.get(f.organization_id)?.devices?.residents_without_fall_coverage == null).map((f) => f.name).join(', ') + ', so residents in house are unknown there.'
                  : FA
                    ? uncCount + ' of your residents. Each needs a person to check on them until the device is fixed.'
                    : uncCount + ' residents in total. Each building has been told.'}
              </span>
            </div>
            {isDev && (
              <div style={sx('font-size:14px;color:#54606C;margin-top:3px;')}>
                <span>Initials only for Dev (minimum necessary). The eye reveals a name, asks why, and logs it to the Truth Ledger.</span>
              </div>
            )}
          </div>

          {/* V6 Role checks (not for a Facility Admin): the QA crawls signing in as every role. */}
          {!FA && <RoleChecks report={qaRuns.data ?? null} loading={qaRuns.loading} error={!!qaRuns.error} plain={plain} />}

          {/* V6 Safety drills (not for a Facility Admin): the care canary's steps, last run. */}
          {!FA && (() => {
            const last = canary.data?.care.last ?? null;
            const steps = last?.steps ?? [];
            const fails = steps.filter((d) => d.status === 'fail');
            const when = last ? new Date(last.ranAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
            return (
              <div data-screen-label="Pulse · Safety drills" data-testid="pulse-safety-drills" style={sx('box-sizing:border-box;display:flex;flex-direction:column;gap:6px;margin-bottom:12px;border:1px solid rgba(34,39,46,.14);border-radius:12px;background:#FCFAF4;padding:10px 12px;min-width:0;')}>
                <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#22272E;')}>Safety drills</span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    {canary.error ? plain('The drill results could not be read (GET /api/pulse/canary).', 'The drill results could not be read.') : !last ? (canary.loading ? '' : 'No drill has run yet.') : `${steps.length - fails.length} of ${steps.length} passed in the last run, at ${when}.`}
                  </span>
                </div>
                {fails.length > 0 && (
                  <div role="alert" style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:9px 11px;border-radius:9px;background:rgba(179,38,30,.07);border:1px solid rgba(179,38,30,.3);')}>
                    <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#B3261E;white-space:nowrap;')}>CARE PATH AT RISK</span>
                    <span style={sx('flex:1 1 260px;min-width:0;font-size:16px;line-height:1.4;color:#22272E;')}>{fails.map((d) => drillName(d.name) + (d.detail ? ': ' + d.detail : '')).join(' ')}</span>
                  </div>
                )}
                {steps.length > 0 && (
                  <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:0 18px;')}>
                    {steps.map((d, i) => (
                      <div key={i} style={sx('display:flex;align-items:center;gap:8px;min-height:30px;padding:3px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                        <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:4px;padding:2px 6px;' + (d.status === 'pass' ? 'color:#1F6B52;background:rgba(47,138,111,.13);' : 'color:#B3261E;background:rgba(179,38,30,.1);'))}>{d.status === 'pass' ? 'PASS' : 'FAIL'}</span>
                        <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;line-height:1.35;color:#22272E;text-wrap:pretty;')}>{drillName(d.name)}</span>
                        <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{when}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Aithora systems (not for a Facility Admin) */}
          {!FA && (
            <div data-screen-label="Pulse · Systems" style={sx(CARD)} data-testid="pulse-systems">
              <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;margin-bottom:6px;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;color:#22272E;')}>Aithora systems</span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{health.error ? 'Service health could not be read.' : svc.filter((x) => x.rank < 2).length + ' of ' + svc.length + ' need attention'}</span>
                </span>
              </div>
              {svc.map(({ c }) => (
                <SystemRow key={c.name} c={c} problems={problems.map((q) => q.p)} onOpen={(id) => setOpenId(id)} />
              ))}
            </div>
          )}
        </div>

        {!wall && (
          <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:10px;min-width:0;')}>
            <div data-screen-label="Pulse · Act now" style={sx(CARD)} data-testid="pulse-act-now">
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:4px;')}>
                <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#22272E;')}>Act now</span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>
                    {probs.error
                      ? plain('not served yet: the problem queue needs GET /pulse-v3/problems')
                      : FA
                        ? problems.length
                          ? problems.length + ' open at your building'
                          : 'Nothing Aithora-side is affecting your building'
                        : problems.length + ' open · ' + unowned + ' unowned'}
                  </span>
                </span>
                <span style={sx('flex:1 1 10px;')} />
                {!FA && <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>J K move · A ack · T take · Enter open</span>}
              </div>
              {problems.map((q, i) => {
                const p = q.p;
                const k = p.severity;
                const remain = q.remain;
                const hot = remain !== null && remain < 30;
                const o = ownerOf(p);
                const inert = past || !canWrite;
                return (
                  <div
                    key={p.id}
                    onClick={(e) => {
                      pre(e);
                      setOpenId(p.id);
                      setSel(i);
                    }}
                    style={sx(
                      'display:grid;grid-template-columns:3px minmax(0,1fr) 118px 78px 126px;align-items:center;gap:0 10px;padding:7px 8px;margin:0 -8px;border-top:1px solid rgba(34,39,46,.09);cursor:pointer;border-radius:8px;min-width:0;' +
                        (i === selIdx ? 'background:rgba(47,92,151,.07);box-shadow:inset 0 0 0 1.5px rgba(47,92,151,.35);' : ''),
                    )}
                    data-testid="pulse-problem"
                  >
                    <span style={sx('width:3px;align-self:stretch;border-radius:2px;background:' + QC[k][0] + ';')} />
                    <span style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                      <span style={sx('display:flex;align-items:baseline;gap:8px;min-width:0;white-space:nowrap;overflow:hidden;')}>
                        <span style={sx(qChip(k))}>
                          <span>{QC[k][2]}</span>
                        </span>
                        <span title={p.title} style={sx('font-size:16px;font-weight:700;min-width:0;white-space:normal;overflow-wrap:break-word;')}>
                          <span>{p.title}</span>
                        </span>
                        {/* An anomaly's or SLO's id: small and secondary after the readable title. */}
                        {p.source_id && (
                          <span data-testid="pulse-problem-id" translate="no" style={sx('flex:0 0 auto;font-size:16px;font-weight:600;color:#54606C;font-variant-numeric:tabular-nums;')}>
                            {p.source_id}
                          </span>
                        )}
                      </span>
                      <span title={p.people} style={sx('font-size:14px;line-height:1.4;overflow-wrap:break-word;max-width:100%;font-weight:' + (p.exposed ? '700;color:#B3261E;' : '500;color:#3C454F;'))}>
                        <span>{p.people}</span>
                      </span>
                    </span>
                    <span style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:800;letter-spacing:-.01em;white-space:nowrap;color:' + (hot || (remain !== null && remain <= 0) ? '#B3261E' : '#22272E') + ';')}>
                        <span>{clockOf(remain, q.age)}</span>
                      </span>
                      <span title={clockNote(p, remain)} style={sx('font-size:14px;color:#54606C;line-height:1.35;overflow-wrap:break-word;')}>
                        <span>{clockNote(p, remain)}</span>
                      </span>
                    </span>
                    <span style={sx('font-size:14px;overflow-wrap:break-word;min-width:0;font-weight:700;color:' + (o ? '#3C454F' : '#B3261E') + ';')}>
                      <span>{o ? (o === 'you' ? 'You' : o) : 'Unowned'}</span>
                    </span>
                    {psCanWrite && <a
                      href="#"
                      aria-disabled={inert ? 'true' : undefined}
                      title={inert && !past ? writeTitle : undefined}
                      onClick={(e) => {
                        pre(e);
                        acts.run(p);
                      }}
                      style={sx(
                        'display:inline-flex;justify-content:center;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;' +
                          (past ? 'background:rgba(34,39,46,.06);color:#8B949E;pointer-events:none;' : i === 0 ? 'background:#22272E;color:#fff;' : 'background:#fff;color:#22272E;border:1px solid rgba(34,39,46,.2);'),
                      )}
                    >
                      <span>{acts.label(p)}</span>
                    </a>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!wall && (
          <div data-screen-label="Pulse · Coming up" style={sx(CARD)} data-testid="pulse-coming-up">
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;margin-bottom:2px;')}>
              <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#22272E;')}>Coming up</span>
              <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:.07em;color:#5B6B7C;background:rgba(91,107,124,.13);')}>PROJECTED</span>
            </div>
            <div style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;margin-bottom:4px;')}>
              <span>{trackLine(fore.data?.track_record ?? null, !!fore.error, plain)}</span>
            </div>
            {(fore.data?.forecasts ?? [])
              .filter((f) => !f.problem_id || problems.some((q) => q.p.id === f.problem_id))
              .map((f) => (
                <ForecastRow key={f.id} f={f} now={viewAt} past={past} problem={problems.find((q) => q.p.id === f.problem_id)?.p ?? null} acts={acts} canAct={psCanWrite} open={(id) => setOpenId(id)} />
              ))}
          </div>
        )}
      </div>

      {openProblem && (
        <StationProblemDrawer
          q={openProblem}
          inc={openInc}
          now={viewAt}
          past={past}
          owner={ownerOf(openProblem.p)}
          actLabel={acts.label(openProblem.p)}
          canWrite={canWrite}
          canAct={psCanWrite}
          writeTitle={writeTitle}
          onAct={() => acts.run(openProblem.p)}
          onAck={() => ack(openProblem.p)}
          onClose={() => setOpenId(null)}
          onOpenIncident={onOpenIncident}
        />
      )}

      {pathDrawer && (
        <>
          <div onClick={(e) => { pre(e); setPathOpen(null); }} style={sx('position:fixed;inset:0;z-index:80;background:rgba(21,27,34,.28);')} />
          <div role="dialog" aria-label={pathDrawer.title} data-screen-label="Pulse · Path" style={sx('position:fixed;top:0;right:0;bottom:0;z-index:81;width:min(480px,100%);box-sizing:border-box;background:#FCFAF4;border-left:1px solid rgba(34,39,46,.18);box-shadow:-18px 0 40px rgba(21,27,34,.18);overflow-y:auto;padding:16px 18px;display:flex;flex-direction:column;gap:10px;')}>
            <div style={sx('display:flex;align-items:flex-start;gap:10px;')}>
              <span style={sx('flex:1 1 auto;font-size:16px;font-weight:800;line-height:1.3;')}>
                <span>{pathDrawer.title}</span>
              </span>
              <a href="#" onClick={(e) => { pre(e); setPathOpen(null); }} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:#fff;color:#22272E;border:1px solid rgba(34,39,46,.2);')}>
                Close · Esc
              </a>
            </div>
            <div style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>
              <span>{pathDrawer.sub}</span>
            </div>
            {pathDrawer.steps.map((x) => (
              <div key={x.n} style={sx('display:grid;grid-template-columns:22px minmax(0,1fr) auto;gap:8px;align-items:baseline;padding:7px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:800;color:#54606C;')}>
                  <span>{x.n}</span>
                </span>
                <span style={sx('min-width:0;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;')}>
                    <span>{x.t}</span>
                  </span>
                  <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.4;')}>
                    <span>{x.note}</span>
                  </span>
                </span>
                <span style={sx(pathChip(x.k))}>
                  <span>{PC[x.k][2]}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {handOpen && (
        <>
          <div onClick={(e) => { pre(e); setHandOpen(false); }} style={sx('position:fixed;inset:0;z-index:80;background:rgba(21,27,34,.32);')} />
          <div role="dialog" aria-label="Shift handover" data-screen-label="Pulse · Handover" style={sx('position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:81;width:min(600px,calc(100% - 32px));max-height:calc(100% - 48px);overflow-y:auto;box-sizing:border-box;background:#FCFAF4;border-radius:14px;box-shadow:0 24px 60px rgba(21,27,34,.28);padding:18px 20px;display:flex;flex-direction:column;gap:10px;')}>
            <div style={sx('font-size:17px;font-weight:800;')}>Shift handover</div>
            <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
              <span>{'Written from the live record at ' + hm(now) + '. ' + problems.length + ' open, ' + problems.filter((q) => q.p.owner).length + ' owned, ' + unowned + ' unowned. The next person signs to accept; the signature is logged.'}</span>
            </div>
            {problems.map((q) => {
              const o = ownerOf(q.p);
              return (
                <div key={q.p.id} style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;padding:7px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                  <span style={sx(qChip(q.p.severity))}>
                    <span>{QC[q.p.severity][2]}</span>
                  </span>
                  <span style={sx('flex:1 1 220px;min-width:0;font-size:16px;font-weight:700;text-wrap:pretty;')}>
                    <span>{q.p.title}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:700;white-space:nowrap;color:' + (o ? '#3C454F' : '#B3261E') + ';')}>
                    <span>{o ? (o === 'you' ? 'You' : o) : 'Unowned'}</span>
                  </span>
                  <span style={sx('flex:1 1 100%;font-size:14px;color:#3C454F;')}>
                    <span>{q.remain !== null ? 'Next: ' + q.p.due_what + ' in ' + dur(q.remain) + '. ' + q.p.people : q.p.people}</span>
                  </span>
                </div>
              );
            })}
            <div style={sx('font-size:16px;font-weight:700;color:' + (hand.data?.signed_at ? '#1F6B52' : '#B3261E') + ';')}>
              <span>{hand.data?.signed_at ? 'Accepted by ' + (hand.data.signed_by?.user_id === me ? 'you' : hand.data.signed_by?.name ?? 'someone') + ' at ' + hm(Date.parse(hand.data.signed_at)) + '. Logged.' : hand.error ? plain('Handover signing is not served yet (POST /pulse-v3/handover/sign).') : 'Not accepted yet.'}</span>
            </div>
            <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
              <a
                href="#"
                aria-disabled={!canWrite ? 'true' : undefined}
                title={writeTitle}
                onClick={(e) => {
                  pre(e);
                  if (!canWrite || hand.data?.signed_at) return;
                  pulseHandoverSign(client, { body: { confirm: true } }).then((r) => hand.setData(() => r), fail);
                }}
                style={sx(BTN_DARK)}
              >
                <span>{hand.data?.signed_at ? 'Signed' : 'Sign and accept handover'}</span>
              </a>
              <a href="#" onClick={(e) => { pre(e); setHandOpen(false); }} style={sx(BTN_LIGHT)}>
                Close
              </a>
            </div>
          </div>
        </>
      )}

      {ask && (
        <div style={sx('position:fixed;inset:0;z-index:80;background:rgba(34,39,46,.32);display:flex;align-items:center;justify-content:center;padding:20px;')}>
          <div role="dialog" aria-label="Show this resident's name?" style={sx('width:100%;max-width:420px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:12px;box-shadow:0 18px 40px rgba(34,39,46,.2);padding:18px;display:flex;flex-direction:column;gap:10px;')}>
            <div style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Show this resident's name?</div>
            <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>
              <span>{ask.who}</span>. The reveal is sealed to the Truth Ledger with your name, the time and your reason.
            </div>
            <input
              value={askWhy}
              aria-label="Reason"
              onChange={(e) => setAskWhy(e.target.value)}
              placeholder="Why do you need it? e.g. alert routed to the wrong resident"
              style={sx('height:36px;border:1px solid rgba(34,39,46,.2);border-radius:7px;padding:0 10px;font-size:16px;font-family:inherit;background:#fff;color:#22272E;')}
            />
            <div style={sx('display:flex;justify-content:flex-end;gap:8px;')}>
              <a href="#" onClick={(e) => { pre(e); setAsk(null); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;color:#3C454F;text-decoration:none;')}>
                Cancel
              </a>
              <a
                href="#"
                aria-disabled={askWhy.trim().length < 8 ? 'true' : undefined}
                onClick={(e) => {
                  pre(e);
                  const why = askWhy.trim();
                  if (why.length < 8) return;
                  const t = ask.token;
                  pulseReveal(client, { body: { token: t, reason: why, confirm: true } }).then(
                    (r) => {
                      setRevealed((o) => ({ ...o, [t]: r.name }));
                      setAsk(null);
                    },
                    fail,
                  );
                }}
                style={sx('display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;' + (askWhy.trim().length >= 8 ? 'background:#22272E;color:#F7F3EA;' : 'background:rgba(34,39,46,.08);color:#54606C;cursor:not-allowed;'))}
              >
                Reveal and log
              </a>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.label}
          onConfirm={confirm.go}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

export function clockOf(remain: number | null, age: number): string {
  return remain === null ? 'open ' + dur(age) : remain > 0 ? dur(remain) : 'overdue ' + dur(-remain);
}
export function clockNote(p: PulseProblem, remain: number | null): string {
  if (remain === null) return p.severity === 'unknown' ? 'risk until a test passes' : 'no deadline set';
  const what = (p.due_what ?? '').replace(/^crosses/, remain > 0 ? 'crosses' : 'crossed').replace(/^escalates/, remain > 0 ? 'escalates' : 'escalated');
  return (remain > 0 ? 'until it ' : 'since it ') + what;
}
function trackLine(t: { resolved: number; hits: number } | null, missing: boolean, plain: (s: string) => string): string {
  if (missing) return plain('Forecasts are not served yet (GET /pulse-v3/forecasts), so nothing is projected here.');
  if (!t || t.resolved < 10) return 'Track record: ' + (t && t.resolved ? t.resolved + ' resolved so far' : 'none resolved yet') + '. Shown as a hit rate once 10 projections have resolved.';
  return 'Track record: ' + t.hits + ' of ' + t.resolved + ' projections came true.';
}

function SystemRow({ c, problems, onOpen }: { c: HealthCheck; problems: PulseProblem[]; onOpen: (id: string) => void }) {
  const k = c.status;
  const trace = c.data?.p99Trace ?? null;
  const lim = c.data?.limitMs ?? null;
  const vals = trace ? trace.map((y) => (y === null || y === undefined ? null : Number(y))) : null;
  const nums = (vals ?? []).filter((y): y is number => y !== null);
  const top = Math.max(...nums, lim ? lim * 1.3 : 1);
  const py = (y: number) => (29 - (y / top) * 26).toFixed(1);
  // The newest reading there is (this minute's beat may not be in yet), and the line broken
  // where a minute has no reading: a gap, never a drop to zero (AF-1615).
  const last = nums.length ? [...(vals ?? [])].reverse().find((y): y is number => y !== null) ?? null : null;
  const n = vals ? vals.length : 0;
  const runs: string[] = [];
  let run: string[] = [];
  (vals ?? []).forEach((y, i) => {
    if (y === null) { if (run.length) runs.push(run.join(' ')); run = []; return; }
    run.push(((i * 300) / Math.max(1, n - 1)).toFixed(1) + ',' + py(y));
  });
  if (run.length) runs.push(run.join(' '));
  const label = c.data?.traceLabel ?? 'p99';
  const unit = c.data?.traceUnit ?? 'ms';
  const reading = (y: number) => label + ' ' + (unit === '%' ? Math.round(y * 10) / 10 : Math.round(y)) + unit;
  const color = k === 'down' ? '#B3261E' : k === 'degraded' ? '#8A5A00' : '#2F5C97';
  const deps = c.data?.carries ? carriesWords(c.data.carries) : c.detail;
  const linked = problems.find((p) => p.title.split(' · ')[0] === c.name);
  const bg = k === 'down' ? 'rgba(179,38,30,.07)' : k === 'degraded' ? 'rgba(184,134,11,.08)' : 'transparent';
  return (
    <div
      onClick={linked ? (e) => { pre(e); onOpen(linked.id); } : undefined}
      style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1.5fr);gap:8px;align-items:center;padding:4px 6px;border-radius:6px;box-sizing:border-box;min-width:0;border-top:1px solid rgba(34,39,46,.07);background:' + bg + ';')}
      data-testid="pulse-system"
    >
      <span style={sx('min-width:0;display:flex;align-items:center;gap:6px;')}>
        <span style={sx('flex:0 0 8px;width:8px;height:8px;border-radius:50%;background:' + (QC[k as QKey] ?? QC.unknown)[0] + ';')} />
        <span style={sx('font-size:14px;font-weight:700;overflow-wrap:break-word;')}>
          <span>{c.name}</span>
        </span>
      </span>
      <span style={sx('min-width:0;')}>
        {vals ? (
          <svg viewBox="0 0 300 30" preserveAspectRatio="none" style={sx('display:block;width:100%;height:22px;overflow:visible;')} aria-hidden="true">
            {lim ? <line x1="0" y1={py(lim)} x2="300" y2={py(lim)} stroke="rgba(179,38,30,.5)" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" /> : null}
            {runs.map((p, i) => (
              <polyline key={i} points={p} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
        ) : (
          <span style={sx('font-size:14px;color:#54606C;')}>not measured</span>
        )}
      </span>
      <span style={sx('min-width:0;font-size:14px;color:#3C454F;line-height:1.3;overflow-wrap:break-word;')} title={deps}>
        <span>{last === null ? '–' : reading(last)}</span>
        {' · '}
        <span>{deps}</span>
      </span>
    </div>
  );
}

function ForecastRow({ f, now, past, problem, acts, canAct, open }: { f: PulseForecast; now: number; past: boolean; problem: PulseProblem | null; acts: Acts; canAct: boolean; open: (id: string) => void }) {
  const mins = f.at ? (Date.parse(f.at) - now) / 60000 : null;
  const red = mins !== null && mins < 60;
  const hasAct = !!f.action_label && !past && canAct;
  const label = f.action === 'tell' && problem?.told_at ? 'Sent' : f.action_label;
  return (
    <div style={sx('padding:8px 0;border-top:1px solid rgba(34,39,46,.09);min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('flex:0 0 auto;font-variant-numeric:tabular-nums;font-size:16px;font-weight:800;white-space:nowrap;color:' + (red ? '#B3261E' : mins === null ? '#54606C' : '#22272E') + ';')}>
          <span>{mins === null ? 'n/a' : 'in ' + dur(mins)}</span>
        </span>
        <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:700;line-height:1.35;text-wrap:pretty;')}>
          <span>{f.what}</span>
        </span>
        {hasAct && (
          <a
            href="#"
            onClick={(e) => {
              pre(e);
              if (!problem) return;
              if (f.action === 'tell') acts.run(problem);
              else open(problem.id);
            }}
            style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;height:26px;background:#fff;color:#22272E;border:1px solid rgba(34,39,46,.2);')}
          >
            <span>{label}</span>
          </a>
        )}
      </div>
      <span style={sx('display:block;height:4px;border-radius:2px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:6px;')}>
        <span style={sx('display:block;height:100%;border-radius:2px;background:' + (red ? '#B3261E' : '#5B6B7C') + ';width:' + (mins === null ? 0 : Math.max(3, Math.min(100, Math.round((1 - mins / 2880) * 100)))) + '%;transition:width .6s;')} />
      </span>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:4px;text-wrap:pretty;')}>
        <span>{f.basis}</span>
      </div>
    </div>
  );
}

